import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { generateAdvertorial, createAdvertorial } from "@/lib/api";

const CATS = ["music", "athletes", "fashion", "shoes", "cars", "travel", "technology", "business", "media", "food"];

export default function AdvertorialStudio() {
  const [form, setForm] = useState({
    brand: "", product: "", angle: "", category: "fashion", image: "", cta_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    if (!form.brand.trim() || !form.product.trim()) {
      toast.error("Add a brand and product first.");
      return;
    }
    setLoading(true);
    setDraft(null);
    setPublished(null);
    try {
      const d = await generateAdvertorial(form.brand, form.product, form.angle, form.category);
      setDraft(d);
      toast.success("Advertorial drafted.");
    } catch (e) {
      toast.error("Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!draft) return;
    setPublishing(true);
    try {
      const saved = await createAdvertorial({
        brand: form.brand,
        product: form.product,
        category: form.category,
        title: draft.title,
        dek: draft.dek,
        body: draft.body,
        image: form.image || draft.image,
        cta_text: draft.cta_text,
        cta_url: form.cta_url || "#",
      });
      setPublished(saved);
      toast.success("Published to the Sponsored feed.");
    } catch (e) {
      toast.error("Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16">
      <div className="flex items-center gap-3 mb-3">
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal">Feature</Link>
        <span className="text-white/20">/</span>
        <span className="font-mono text-xs uppercase tracking-widest text-signal">Advertorial</span>
      </div>
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight leading-none">
        Advertorial Builder
      </h1>
      <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
        Turn a brand into a culture-first sponsored story. AI writes it in the NERO SUN
        voice — then publish it straight to the Sponsored feed.
      </p>

      <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={label}>Brand</label>
            <input data-testid="ad-brand-input" value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="e.g. Atelier Noir" className={inputCls} />
          </div>
          <div>
            <label className={label}>Product / Offer</label>
            <input data-testid="ad-product-input" value={form.product} onChange={(e) => set("product", e.target.value)} placeholder="e.g. The Obsidian leather collection" className={inputCls} />
          </div>
        </div>
        <div>
          <label className={label}>Angle (optional)</label>
          <input data-testid="ad-angle-input" value={form.angle} onChange={(e) => set("angle", e.target.value)} placeholder="e.g. quiet luxury, the new flex" className={inputCls} />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={label}>Image URL (optional)</label>
            <input data-testid="ad-image-input" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://… (leave blank for default)" className={inputCls} />
          </div>
          <div>
            <label className={label}>Call-to-action link</label>
            <input data-testid="ad-cta-input" value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://brand.com/shop" className={inputCls} />
          </div>
        </div>
        <div>
          <label className={label}>Section</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`ad-cat-${c}`}
                onClick={() => set("category", c)}
                className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  form.category === c ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="ad-generate-btn"
          onClick={handleGenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-white text-black font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Drafting…" : "Generate Advertorial"}
        </button>
      </div>

      {draft && (
        <div className="mt-12 animate-rise" data-testid="ad-draft">
          <div className="bg-white text-black inline-flex font-mono text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 mb-4">
            Sponsored Preview · {form.brand}
          </div>
          <div className="relative h-64 overflow-hidden border border-white/10">
            <img src={form.image || draft.image || "https://images.unsplash.com/photo-1767036840795-359e1c5fba16?w=1200&q=80"} alt={draft.title} className="w-full h-full object-cover img-treatment" />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
          </div>
          <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl mt-6 leading-[0.95]" data-testid="ad-draft-title">
            {draft.title}
          </h2>
          <p className="font-display text-xl text-foreground/90 mt-4">{draft.dek}</p>
          <div className="space-y-5 mt-8">
            {(draft.body || "").split("\n\n").map((p, i) => (
              <p key={i} className="text-foreground/85 text-lg leading-relaxed">{p}</p>
            ))}
          </div>
          <span className="inline-block mt-8 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3">
            {draft.cta_text}
          </span>

          <div className="mt-10 border-t border-white/10 pt-6">
            {published ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="ad-published">
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-rankup">
                  <Check className="w-4 h-4" /> Published
                </span>
                <Link to={`/advertorial/${published.slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-signal hover:underline">
                  View live page <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <button
                data-testid="ad-publish-btn"
                onClick={handlePublish}
                disabled={publishing}
                className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {publishing ? "Publishing…" : "Publish to Sponsored Feed"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
